import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const SubCategory = () => {
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_API_BASE_URL; // ✅ Base URL from .env

  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  // ✅ Fetch Subcategories
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const res = await axios.get(`${baseUrl}/subcategory`);
        if (res.data?.success) {
          setSubCategories(res.data.data);
        } else {
          console.error("Unexpected API response:", res.data);
          alert("Failed to fetch subcategories!");
        }
      } catch (err) {
        console.error("Error fetching subcategories:", err);
        alert("Failed to fetch subcategories!");
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
  }, [baseUrl]);

  // ✅ Toggle Subcategory status (active/inactive)
  const toggleStatus = async (id: string) => {
    try {
      const res = await axios.patch(`${baseUrl}/subcategory/${id}/toggle-status`);
      if (res.data.success) {
        setSubCategories((prev) =>
          prev.map((sub) =>
            (sub.id || sub._id) === id
              ? { ...sub, status: sub.status === "active" ? "inactive" : "active" }
              : sub
          )
        );
      } else {
        alert(res.data.message || "Failed to update status!");
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status!");
    }
  };

  // ✅ Delete Subcategory
  const deleteSubCategory = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;
    try {
      const res = await axios.delete(`${baseUrl}/subcategory/${id}`);
      if (res.data.success) {
        setSubCategories((prev) => prev.filter((sub) => (sub.id || sub._id) !== id));
        alert("Subcategory deleted successfully!");
      } else {
        alert(res.data.message || "Failed to delete subcategory!");
      }
    } catch (err) {
      console.error("Error deleting subcategory:", err);
      alert("Failed to delete subcategory!");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading...</div>;

  // ✅ Search & Pagination
  const filtered = subCategories.filter((sub) =>
    sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.categoryId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalEntries = filtered.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + entriesPerPage, totalEntries);
  const currentSubCategories = filtered.slice(startIndex, endIndex);

  return (
    <div className="main-container container-fluid">
      {/* HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              All Subcategories List
            </li>
          </ol>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/settings/subcategory/add")}
        >
          <i className="icon-plus"></i> Add Subcategory
        </button>
      </div>

      {/* TABLE CARD */}
      <div className="row">
        <div className="col-12">
          <div className="card table-card">
            <div className="card-body">
              <div className="table-responsive">
                {/* Show Entries + Search */}
                <div className="d-flex justify-content-between mb-3">
                  <div>
                    Show{" "}
                    <select
                      className="form-select form-select-sm d-inline-block"
                      style={{ width: "auto" }}
                      value={entriesPerPage}
                      onChange={(e) => {
                        setEntriesPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>{" "}
                    entries
                  </div>
                  <div>
                    Search:{" "}
                    <input
                      type="search"
                      className="form-control form-control-sm d-inline-block"
                      style={{ width: "200px" }}
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </div>

                {/* TABLE */}
                <table className="table dataTable no-footer">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Main Category</th>
                      <th>Subcategory</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentSubCategories.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No subcategories found.
                        </td>
                      </tr>
                    ) : (
                      currentSubCategories.map((sub, idx) => (
                        <tr key={sub.id || sub._id}>
                          <td>{startIndex + idx + 1}</td>
                          <td>
                            {sub.categoryId?.name || (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>
                          <td>{sub.name}</td>
                          <td>{sub.description}</td>
                          <td>
                            <span
                              onClick={() => toggleStatus(sub._id)}
                              className={`badge ${
                                sub.status === "active"
                                  ? "bg-success text-white fs-14 rounded-2"
                                  : "bg-danger text-white fs-14 rounded-2"
                              }`}
                              style={{ cursor: "pointer" }}
                            >
                              {sub.status === "active" ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="bg-primary-transparent text-primary p-2 rounded-2 text-center fs-16 me-2 border-0"
                              onClick={() =>
                                navigate(`/settings/subcategory/edit/${sub._id}`)
                              }
                            >
                              <i className="fe fe-edit"></i>
                            </button>

                            <button
                              className="bg-danger-transparent text-danger p-2 rounded-2 text-center fs-16 border-0"
                              onClick={() => deleteSubCategory(sub._id)}
                            >
                              <i className="fe fe-trash-2"></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                {/* PAGINATION */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {startIndex + 1} to {endIndex} of {totalEntries} entries
                  </div>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage((p) => p - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <li
                        key={i}
                        className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubCategory;

